// ProImageEditorV2.js - Professional Image Editor with AI Chat Assistant
import React, { useState, useRef, useEffect, useCallback } from 'react';
import './ProImageEditorV2.css';

export default function ProImageEditorV2({
  isOpen,
  onClose,
  onSwitchToVideoEditor,
  onSwitchToChat,
  embedded = false,
  /** Shown in the embedded top bar (e.g. "Adobe Photoshop") */
  embeddedWorkspaceTitle = '',
  /** When set to 'photoshop', prefers Firefly model after /chat/capabilities loads (if configured). */
  defaultImageEditModel = null,
}) {
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement && document.fullscreenElement === containerRef.current);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;
    try {
      if (isFullscreen) {
        await document.exitFullscreen();
      } else {
        await containerRef.current.requestFullscreen();
      }
    } catch (err) {
      console.warn('Fullscreen error:', err);
    }
  }, [isFullscreen]);

  // Canvas and layers
  const canvasRef = useRef(null);
  const [ctx, setCtx] = useState(null);
  const [layers, setLayers] = useState([
    { id: 'layer1', name: 'Background', visible: true, opacity: 100, blendMode: 'normal', locked: false },
  ]);
  const [selectedLayer, setSelectedLayer] = useState('layer1');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const historyIndexRef = useRef(-1);
  useEffect(() => { historyIndexRef.current = historyIndex; }, [historyIndex]);

  // Tools
  const [selectedTool, setSelectedTool] = useState('select');
  const [brushSize, setBrushSize] = useState(10);
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushOpacity, setBrushOpacity] = useState(100);
  const [brushFlow, setBrushFlow] = useState(100);
  const [brushHardness, setBrushHardness] = useState(80); // 0 = soft, 100 = hard
  const [textFontSize, setTextFontSize] = useState(48);
  const [textFontFamily, setTextFontFamily] = useState('sans-serif');
  const [textBold, setTextBold] = useState(false);
  const [textItalic, setTextItalic] = useState(false);
  const [textAnchor, setTextAnchor] = useState(null); // { x, y } when placing text
  const [textDraft, setTextDraft] = useState('');
  const textInputRef = useRef(null);
  const [zoom, setZoom] = useState(100);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef(null);
  const lastDrawPos = useRef(null);
  const gradientStartRef = useRef(null);

  // Selection: rect { type:'rect', x, y, width, height } or lasso { type:'lasso', points: [{x,y},...] }
  const [selection, setSelection] = useState(null);
  const [selectionDraft, setSelectionDraft] = useState(null);
  const [selectionMode, setSelectionMode] = useState('rect'); // 'rect' | 'ellipse' | 'lasso'
  const selectionStartRef = useRef(null); // rect: {x,y}; lasso: unused (points in draft)
  const lassoPointsRef = useRef(null);   // lasso points while drawing

  // AI Chat Assistant
  const [aiMessages, setAiMessages] = useState([
    { id: 1, sender: 'ai', text: "👋 Hi! Upload an image and tell me what to do—enhance, remove background, vintage, upscale. With Nano Banana you can also **Generate** a new image from text (no photo needed). Say \"switch to video editor\" or \"back to chat\" anytime." }
  ]);
  const [aiInput, setAiInput] = useState('');
  const [aiProcessing, setAiProcessing] = useState(false);
  const [imageEditModel, setImageEditModel] = useState('dalle'); // 'dalle' | 'nano-banana' | 'photoshop'
  /** Nano Banana latency vs quality: fast (0.5K jpeg), balanced (1K jpeg), quality (1K png) */
  const [nanoImageSpeed, setNanoImageSpeed] = useState('fast');
  const [falStatus, setFalStatus] = useState(null); // null | { ok: true } | { ok: false, error }
  const [photoshopImageEditCap, setPhotoshopImageEditCap] = useState(false);
  const [photoshopStatus, setPhotoshopStatus] = useState(null);
  const [capabilitiesOpen, setCapabilitiesOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [fixImperfectionsOpen, setFixImperfectionsOpen] = useState(false);
  const [isolateObjectsOpen, setIsolateObjectsOpen] = useState(false);
  const [artworkDesignOpen, setArtworkDesignOpen] = useState(false);
  const aiChatRef = useRef(null);

  // Prefer Nano Banana when FAL_KEY is set; verify key is accepted by fal.ai (for Image Editor sandbox)
  useEffect(() => {
    if (!isOpen) return;
    let base = (process.env.REACT_APP_API_URL || '').toString().trim().replace(/\/+$/, '');
    if (!base && typeof window !== 'undefined' && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(window.location?.origin || ''))
      base = 'http://localhost:8080';
    base = base || (typeof window !== 'undefined' ? window.location?.origin : '') || '';
    const tryFetch = (url) => fetch(url).then((r) => r.json());
    tryFetch(`${base}/chat/capabilities`).then((cap) => {
      const ps = Boolean(cap?.photoshopImageEdit);
      setPhotoshopImageEditCap(ps);
      if (defaultImageEditModel === 'photoshop') {
        if (ps) setImageEditModel('photoshop');
      } else if (cap && cap.fal) {
        setImageEditModel('nano-banana');
      }
      if (ps) {
        tryFetch(`${base}/chat/photoshop-status`)
          .then((data) => setPhotoshopStatus(data?.ok === true ? { ok: true } : { ok: false, error: data?.error || 'Auth check failed' }))
          .catch(() => setPhotoshopStatus({ ok: false, error: 'Could not reach /chat/photoshop-status' }));
      } else {
        setPhotoshopStatus(null);
      }
    }).catch(() => {});
    tryFetch(`${base}/chat/fal-status`)
      .then((data) => setFalStatus(data || { ok: false, error: 'Unknown response' }))
      .catch(() => {
        const onLocalhost = typeof window !== 'undefined' && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(window.location?.origin || '');
        if (onLocalhost && base !== 'http://localhost:8080')
          tryFetch('http://localhost:8080/chat/fal-status').then((data) => setFalStatus(data || { ok: false })).catch(() => setFalStatus({ ok: false, error: 'Network error. Start the backend (e.g. npm start in backend) and set REACT_APP_API_URL=http://localhost:8080 in frontend/.env' }));
        else
          setFalStatus({ ok: false, error: 'Network error. Is the backend running? Set REACT_APP_API_URL in frontend/.env to your backend URL.' });
      });
  }, [isOpen, defaultImageEditModel]);

  useEffect(() => {
    if (imageEditModel === 'photoshop' && !photoshopImageEditCap) {
      setImageEditModel('dalle');
    }
  }, [imageEditModel, photoshopImageEditCap]);

  // Adjustments
  const [adjustments, setAdjustments] = useState({
    brightness: 0,
    contrast: 0,
    saturation: 0,
    hue: 0,
    temperature: 0,
    exposure: 0,
    highlights: 0,
    shadows: 0,
    clarity: 0,
    vibrance: 0,
  });

  // Filters
  const [filters, setFilters] = useState({
    blur: 0,
    sharpen: 0,
    noise: 0,
    vignette: 0,
    grain: 0,
  });

  // Curves (Photoshop-style: shadows, midtones, highlights)
  const [curves, setCurves] = useState({ shadows: 0, midtones: 0, highlights: 0 });

  const BLEND_MODES = ['normal', 'multiply', 'screen', 'overlay', 'soft-light', 'hard-light', 'color-dodge', 'color-burn', 'darken', 'lighten', 'difference', 'exclusion'];

  // Image state
  const [imageData, setImageData] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [hasUploadedImage, setHasUploadedImage] = useState(false);

  // Left sidebar collapsed = minimal view (image + typing box only); expand via + icon
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);

  const apiBase = () => {
    let base = (process.env.REACT_APP_API_URL || '').toString().trim().replace(/\/+$/, '');
    if (!base && typeof window !== 'undefined' && /^https?:\/\/localhost(:\d+)?$/i.test(window.location?.origin || ''))
      base = (process.env.REACT_APP_API_URL || '').toString().trim().replace(/\/+$/, '') || 'http://localhost:8080';
    return base || window.location?.origin || '';
  };

  // Initialize canvas
  useEffect(() => {
    if (canvasRef.current && !ctx) {
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      setCtx(context);
      canvas.width = 1920;
      canvas.height = 1080;
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      setHistory([canvas.toDataURL()]);
      setHistoryIndex(0);
      historyIndexRef.current = 0;
    }
  }, [canvasRef, ctx]);

  // Auto-scroll AI chat when new messages or typing indicator
  useEffect(() => {
    if (aiChatRef.current)
      aiChatRef.current.scrollTop = aiChatRef.current.scrollHeight;
  }, [aiMessages.length, aiProcessing]);

  // Focus text input when placing text
  useEffect(() => {
    if (textAnchor && textInputRef.current) textInputRef.current.focus();
  }, [textAnchor]);

  // Escape: clear selection or cancel text
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setTextAnchor(null);
        setTextDraft('');
        setSelection(null);
        setSelectionDraft(null);
        selectionStartRef.current = null;
        lassoPointsRef.current = null;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);


  // Create mask image: black = area to edit (selection), white = keep. Backend convention.
  const createSelectionMaskBlob = (canvas, sel) => {
    if (!sel) return null;
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = canvas.width;
    maskCanvas.height = canvas.height;
    const mctx = maskCanvas.getContext('2d', { willReadFrequently: true });
    mctx.fillStyle = 'white';
    mctx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
    mctx.fillStyle = 'black';
    if (sel.type === 'lasso' && sel.points && sel.points.length >= 3) {
      mctx.beginPath();
      mctx.moveTo(sel.points[0].x, sel.points[0].y);
      for (let i = 1; i < sel.points.length; i++) mctx.lineTo(sel.points[i].x, sel.points[i].y);
      mctx.closePath();
      mctx.fill();
    } else {
      const x = sel.x ?? 0, y = sel.y ?? 0, w = sel.width ?? 0, h = sel.height ?? 0;
      if (w <= 0 || h <= 0) return null;
      const x2 = Math.max(0, Math.min(x, canvas.width - 1));
      const y2 = Math.max(0, Math.min(y, canvas.height - 1));
      const w2 = Math.max(1, Math.min(w, canvas.width - x2));
      const h2 = Math.max(1, Math.min(h, canvas.height - y2));
      mctx.fillRect(x2, y2, w2, h2);
    }
    return new Promise((res) => maskCanvas.toBlob(res, 'image/png'));
  };

  const hasValidSelection = (sel) => {
    if (!sel) return false;
    if (sel.type === 'lasso') return sel.points && sel.points.length >= 3;
    return (sel.width > 0 && sel.height > 0); // rect or ellipse
  };

  // Call backend AI image edit (DALL-E): send canvas + prompt, optional mask for selection-only edit
  const performAIImageEdit = async (promptText) => {
    const canvas = canvasRef.current;
    if (!canvas || !ctx) return { ok: false, error: 'No canvas' };
    const w = canvas.width;
    const h = canvas.height;
    if (!w || !h) return { ok: false, error: 'Canvas is empty. Upload an image first.' };

    const useSelection = hasValidSelection(selection);

    return new Promise((resolve) => {
      canvas.toBlob(
        async (blob) => {
          if (!blob) {
            resolve({ ok: false, error: 'Failed to get image data' });
            return;
          }
          if (blob.size === 0) {
            resolve({ ok: false, error: 'Image is empty. Upload or draw an image first.' });
            return;
          }
          const base = apiBase();
          const endpoint = `${base}/chat/edit-image`;
          const formData = new FormData();
          formData.append('image', blob, 'image.png');
          formData.append('prompt', String(promptText || '').trim());
          const modelParam =
            imageEditModel === 'nano-banana' ? 'nano-banana' : imageEditModel === 'photoshop' ? 'photoshop' : 'dalle';
          formData.append('model', modelParam);
          if (imageEditModel === 'nano-banana') formData.append('speed', nanoImageSpeed);

          if (useSelection) {
            const maskBlob = await createSelectionMaskBlob(canvas, selection);
            if (maskBlob) formData.append('mask', maskBlob, 'mask.png');
          }

          try {
            const res = await fetch(endpoint, {
              method: 'POST',
              body: formData,
              credentials: 'include',
            });
            const data = await res.json().catch(() => ({}));
            const imageUrl = data?.imageUrl;
            if (!res.ok || !imageUrl) {
              let errMsg = data?.error || res.statusText || `Request failed (${res.status})`;
              const origin = typeof window !== 'undefined' ? window.location?.origin : '';
              if ((res.status === 404 || res.status === 0) && base && base === origin && !/localhost/i.test(origin))
                errMsg += ' Set REACT_APP_API_URL to your backend URL and rebuild the frontend.';
              resolve({ ok: false, error: errMsg });
              return;
            }
            const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${base}${imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl}`;
            const cacheBust = `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              if (!canvasRef.current || !ctx) {
                resolve({ ok: true });
                return;
              }
              const c = canvasRef.current;
              const context = ctx || c.getContext('2d', { willReadFrequently: true });
              context.clearRect(0, 0, c.width, c.height);
              context.drawImage(img, 0, 0, c.width, c.height);
              setOriginalImage(img);
              addToHistory();
              if (useSelection) setSelection(null);
              resolve({ ok: true });
            };
            img.onerror = () => resolve({ ok: false, error: `Failed to load edited image from ${fullUrl}. Check backend is running and serving /uploads.` });
            img.src = cacheBust;
          } catch (err) {
            const msg = err?.message || String(err);
            const isNetwork = /failed|network|refused|reset|load/i.test(msg);
            resolve({
              ok: false,
              error: isNetwork
                ? `Network error: ${msg}. Is the backend running at ${base}?`
                : (msg || 'AI edit failed'),
            });
          }
        },
        'image/png',
        0.95
      );
    });
  };

  // Text-to-image: generate from prompt only (Nano Banana)
  const performAIGenerate = async (promptText) => {
    const base = apiBase();
    const endpoint = `${base}/chat/generate-image`;
    const safePrompt = String(promptText || '').trim();
    if (!safePrompt) return { ok: false, error: 'Enter a description for the image you want to generate.' };
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prompt: safePrompt, speed: nanoImageSpeed }),
      });
      const data = await res.json().catch(() => ({}));
      const imageUrl = data?.imageUrl;
      if (!res.ok || !imageUrl) {
        return { ok: false, error: data?.error || res.statusText || 'Generate failed' };
      }
      const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${base}${imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl}`;
      const cacheBust = `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          if (!canvasRef.current) { resolve({ ok: true }); return; }
          const c = canvasRef.current;
          c.width = img.naturalWidth;
          c.height = img.naturalHeight;
          const context = c.getContext('2d', { willReadFrequently: true });
          if (context) {
            context.clearRect(0, 0, c.width, c.height);
            context.drawImage(img, 0, 0);
            setCtx(context);
            setOriginalImage(img);
            setImageData(context.getImageData(0, 0, c.width, c.height));
            setHasUploadedImage(true);
            addToHistory();
          }
          resolve({ ok: true });
        };
        img.onerror = () => resolve({ ok: false, error: 'Failed to load generated image.' });
        img.src = cacheBust;
      });
    } catch (err) {
      return { ok: false, error: err?.message || 'Generate failed' };
    }
  };

  // AI Command Processing
  const processAICommand = async (command) => {
    setAiProcessing(true);
    const lowerCommand = (command || '').toLowerCase();

    const userMsg = { id: Date.now(), sender: 'user', text: command };
    setAiMessages(prev => [...prev, userMsg]);

    const addAiReply = (text) => {
      setAiMessages(prev => [...prev, { id: Date.now() + Math.random(), sender: 'ai', text }]);
      setTimeout(() => { if (aiChatRef.current) aiChatRef.current.scrollTop = aiChatRef.current.scrollHeight; }, 100);
    };

    try {
    await new Promise(resolve => setTimeout(resolve, 300));

    let response = '';
    let action = null;

    // Handle editor switching commands
    if (lowerCommand.includes('switch to video') || lowerCommand.includes('open video editor') || lowerCommand.includes('video editor')) {
      response = "🎥 Switching to Professional Video Editor...";
      const aiMsg = { id: Date.now() + 1, sender: 'ai', text: response };
      setAiMessages(prev => [...prev, aiMsg]);
      setAiProcessing(false);
      setTimeout(() => {
        if (onSwitchToVideoEditor) onSwitchToVideoEditor();
      }, 500);
      return;
    } else if (lowerCommand.includes('switch to chat') || lowerCommand.includes('open chat') || lowerCommand.includes('back to chat') || lowerCommand.includes('zolaid computer')) {
      response = "💬 Returning to Zolaid...";
      const aiMsg = { id: Date.now() + 1, sender: 'ai', text: response };
      setAiMessages(prev => [...prev, aiMsg]);
      setAiProcessing(false);
      setTimeout(() => {
        if (onSwitchToChat) onSwitchToChat();
      }, 500);
      return;
    }

    // Natural language command processing
    if (lowerCommand.includes('remove background') || lowerCommand.includes('remove bg')) {
      response = "🎨 Removing background using AI... This may take a moment.";
      action = () => {
        setProcessing(true);
        setTimeout(() => {
          setProcessing(false);
          const followUp = { id: Date.now() + 1, sender: 'ai', text: "✅ Background removed! Your subject is now on a transparent layer." };
          setAiMessages(prev => [...prev, followUp]);
        }, 2000);
      };
    } else if (lowerCommand.includes('brighter') || lowerCommand.includes('brightness')) {
      response = "☀️ Increasing brightness...";
      action = () => {
        setAdjustments(prev => ({ ...prev, brightness: Math.min(100, prev.brightness + 20) }));
      };
    } else if (lowerCommand.includes('darker') || lowerCommand.includes('dim')) {
      response = "🌙 Decreasing brightness...";
      action = () => {
        setAdjustments(prev => ({ ...prev, brightness: Math.max(-100, prev.brightness - 20) }));
      };
    } else if (lowerCommand.includes('contrast')) {
      response = "📊 Adjusting contrast...";
      action = () => {
        setAdjustments(prev => ({ ...prev, contrast: Math.min(100, prev.contrast + 20) }));
      };
    } else if (lowerCommand.includes('saturate') || lowerCommand.includes('vibrant') || lowerCommand.includes('colorful')) {
      response = "🌈 Boosting saturation and vibrance...";
      action = () => {
        setAdjustments(prev => ({
          ...prev,
          saturation: Math.min(100, prev.saturation + 25),
          vibrance: Math.min(100, prev.vibrance + 20)
        }));
      };
    } else if (lowerCommand.includes('desaturate') || lowerCommand.includes('black and white') || lowerCommand.includes('grayscale')) {
      response = "⚫⚪ Converting to black and white...";
      action = () => {
        setAdjustments(prev => ({ ...prev, saturation: -100 }));
      };
    } else if (lowerCommand.includes('warm') || lowerCommand.includes('warmer')) {
      response = "🔥 Applying warm tones...";
      action = () => {
        setAdjustments(prev => ({ ...prev, temperature: Math.min(100, prev.temperature + 30) }));
      };
    } else if (lowerCommand.includes('cool') || lowerCommand.includes('cooler')) {
      response = "❄️ Applying cool tones...";
      action = () => {
        setAdjustments(prev => ({ ...prev, temperature: Math.max(-100, prev.temperature - 30) }));
      };
    } else if (lowerCommand.includes('vintage') || lowerCommand.includes('retro')) {
      response = "📷 Applying vintage film look...";
      action = () => {
        setAdjustments(prev => ({
          ...prev,
          saturation: -20,
          temperature: 15,
          contrast: 10,
        }));
        setFilters(prev => ({ ...prev, grain: 30, vignette: 25 }));
      };
    } else if (lowerCommand.includes('cinematic') || lowerCommand.includes('film')) {
      response = "🎬 Applying cinematic color grade...";
      action = () => {
        setAdjustments(prev => ({
          ...prev,
          contrast: 25,
          saturation: 10,
          highlights: -10,
          shadows: -15,
          clarity: 15,
        }));
      };
    } else if (lowerCommand.includes('blur')) {
      response = "🌫️ Applying blur effect...";
      action = () => {
        setFilters(prev => ({ ...prev, blur: Math.min(100, prev.blur + 20) }));
      };
    } else if (lowerCommand.includes('sharpen') || lowerCommand.includes('sharp')) {
      response = "🔪 Sharpening image...";
      action = () => {
        setFilters(prev => ({ ...prev, sharpen: Math.min(100, prev.sharpen + 25) }));
      };
    } else if (lowerCommand.includes('vignette')) {
      response = "🎭 Adding vignette effect...";
      action = () => {
        setFilters(prev => ({ ...prev, vignette: Math.min(100, prev.vignette + 30) }));
      };
    } else if (lowerCommand.includes('enhance') || lowerCommand.includes('improve')) {
      response = "✨ AI enhancing image with optimal adjustments...";
      action = () => {
        setAdjustments(prev => ({
          ...prev,
          brightness: 5,
          contrast: 15,
          saturation: 10,
          clarity: 20,
          vibrance: 15,
          highlights: -5,
          shadows: 10,
        }));
      };
    } else if (lowerCommand.includes('portrait') || lowerCommand.includes('face') || lowerCommand.includes('retouch')) {
      response = "👤 Applying portrait enhancements...";
      action = () => {
        setAdjustments(prev => ({
          ...prev,
          brightness: 8,
          contrast: 5,
          highlights: -8,
          shadows: 12,
          clarity: -10,
        }));
        setFilters(prev => ({ ...prev, sharpen: 15 }));
      };
    } else if (lowerCommand.includes('reset') || lowerCommand.includes('undo all') || lowerCommand.includes('original')) {
      response = "↶ Resetting all adjustments to original...";
      action = () => {
        setAdjustments({
          brightness: 0,
          contrast: 0,
          saturation: 0,
          hue: 0,
          temperature: 0,
          exposure: 0,
          highlights: 0,
          shadows: 0,
          clarity: 0,
          vibrance: 0,
        });
        setFilters({
          blur: 0,
          sharpen: 0,
          noise: 0,
          vignette: 0,
          grain: 0,
        });
      };
    } else if (lowerCommand.includes('upscale') || lowerCommand.includes('4x') || lowerCommand.includes('enlarge')) {
      response = "🔍 AI upscaling image to 4x resolution... This will take a moment.";
      action = () => {
        setProcessing(true);
        setTimeout(() => {
          setProcessing(false);
          const followUp = { id: Date.now() + 1, sender: 'ai', text: "✅ Image upscaled to 4x! Resolution increased with AI enhancement." };
          setAiMessages(prev => [...prev, followUp]);
        }, 3000);
      };
    } else if (lowerCommand.includes('denoise') || lowerCommand.includes('reduce noise')) {
      response = "🔇 Applying advanced noise reduction...";
      action = () => {
        setFilters(prev => ({ ...prev, noise: -50 }));
      };
    } else if (lowerCommand.includes('color grade') || lowerCommand.includes('color correction')) {
      response = "🎨 Applying professional color grading...";
      action = () => {
        setAdjustments(prev => ({
          ...prev,
          brightness: 5,
          contrast: 12,
          saturation: 8,
          highlights: -5,
          shadows: 8,
          vibrance: 12,
          clarity: 10,
        }));
      };
    } else if (lowerCommand.includes('hdr') || lowerCommand.includes('high dynamic range')) {
      response = "🌟 Creating HDR effect with extended dynamic range...";
      action = () => {
        setAdjustments(prev => ({
          ...prev,
          exposure: 5,
          highlights: -20,
          shadows: 25,
          clarity: 30,
          vibrance: 15,
        }));
      };
    } else if (lowerCommand.includes('skin smooth') || lowerCommand.includes('beauty')) {
      response = "✨ Applying professional skin smoothing...";
      action = () => {
        setAdjustments(prev => ({ ...prev, clarity: -15 }));
        setFilters(prev => ({ ...prev, blur: 8 }));
      };
    } else if (lowerCommand.includes('dramatic') || lowerCommand.includes('moody')) {
      response = "🎭 Creating dramatic, moody atmosphere...";
      action = () => {
        setAdjustments(prev => ({
          ...prev,
          brightness: -10,
          contrast: 35,
          saturation: -15,
          shadows: -20,
          clarity: 25,
        }));
        setFilters(prev => ({ ...prev, vignette: 40 }));
      };
    } else if (lowerCommand.includes('food') || lowerCommand.includes('food photo')) {
      response = "🍽️ Optimizing for food photography...";
      action = () => {
        setAdjustments(prev => ({
          ...prev,
          brightness: 8,
          saturation: 20,
          vibrance: 25,
          temperature: 10,
          clarity: 15,
        }));
      };
    } else if (lowerCommand.includes('landscape') || lowerCommand.includes('nature')) {
      response = "🏞️ Applying landscape enhancement...";
      action = () => {
        setAdjustments(prev => ({
          ...prev,
          saturation: 18,
          vibrance: 20,
          clarity: 20,
          contrast: 12,
        }));
      };
    } else if (lowerCommand.includes('export') || lowerCommand.includes('save') || lowerCommand.includes('download')) {
      response = "💾 Exporting image in high quality...";
      action = () => {
        handleExport();
      };
    } else if (lowerCommand.includes('help') || lowerCommand.includes('what can you')) {
      response = `I can help you with:

📸 **Adjustments**: "Make it brighter", "Increase contrast", "Make colors more vibrant"
🎨 **Effects**: "Remove background", "Add blur", "Sharpen image", "Add vignette"
🎬 **Styles**: "Apply vintage filter", "Make it cinematic", "Portrait mode"
✨ **AI edit**: Describe any change — e.g. "Replace the background with a KFC logo", "Add a sunset", "Put a hat on the person"
🆕 **Generate** (Nano Banana): Click **Generate** or say "generate a sunset" to create a new image from text — no photo needed
💾 **Export**: "Save image", "Export in high quality"
↶ **Reset**: "Reset all changes", "Back to original"
🔄 **Switch**: "Open video editor", "Back to chat"

Just tell me what you'd like to do!`;
    } else {
      // Text-to-image: generate from prompt (Nano Banana only, or when canvas empty)
      const wantsGenerate =
        lowerCommand.includes('generate') ||
        lowerCommand.includes('create image') ||
        lowerCommand.includes('draw me') ||
        lowerCommand.includes('make an image') ||
        lowerCommand.includes('create a picture') ||
        (imageEditModel === 'nano-banana' && !hasUploadedImage);
      if (wantsGenerate && imageEditModel === 'nano-banana') {
        const genMsg = { id: Date.now() + 1, sender: 'ai', text: '🔄 Generating image from your description... Usually 1–2 minutes.' };
        setAiMessages(prev => [...prev, genMsg]);
        setProcessing(true);
        try {
          const result = await performAIGenerate(command);
          const followUp = {
            id: Date.now() + 2,
            sender: 'ai',
            text: result.ok ? '✅ Image generated! It’s now on the canvas.' : `❌ ${result.error || 'Generate failed.'}`,
          };
          setAiMessages(prev => [...prev, followUp]);
        } finally {
          setProcessing(false);
        }
        setAiProcessing(false);
        setTimeout(() => { if (aiChatRef.current) aiChatRef.current.scrollTop = aiChatRef.current.scrollHeight; }, 100);
        return;
      }

      // Unrecognized / freeform command → send to backend AI image edit (DALL-E)
      // Special case: content-aware object removal using current selection as mask.
      const wantsObjectRemoval =
        lowerCommand.includes('remove object') ||
        lowerCommand.includes('erase object') ||
        lowerCommand.includes('content-aware') ||
        lowerCommand.includes('remove this') ||
        lowerCommand.includes('remove selected');

      if (wantsObjectRemoval && !hasValidSelection(selection)) {
        const aiMsg = {
          id: Date.now() + 1,
          sender: 'ai',
          text: "✏️ To remove an object, first draw a selection around it (rect or lasso), then say “remove object”."
        };
        setAiMessages(prev => [...prev, aiMsg]);
        setAiProcessing(false);
        return;
      }

      const applyingMsg = {
        id: Date.now() + 1,
        sender: 'ai',
        text: wantsObjectRemoval
          ? "🪄 Removing the selected object and inpainting the background with AI..."
          : imageEditModel === 'nano-banana'
            ? (nanoImageSpeed === 'quality'
              ? "🔄 Nano Banana… quality mode (~1–2 min)."
              : nanoImageSpeed === 'balanced'
                ? "🔄 Nano Banana… usually under ~1 min."
                : "🔄 Nano Banana… fast mode (~20–60s).")
            : imageEditModel === 'photoshop'
              ? "🔄 Adobe Firefly Generative Fill… often 1–3 minutes."
            : "🔄 Applying your edit with AI... This may take a moment."
      };
      setAiMessages(prev => [...prev, applyingMsg]);
      setProcessing(true);
      try {
        const effectivePrompt = wantsObjectRemoval
          ? "Remove the selected object and fill the area naturally so it matches the surrounding background."
          : command;
        const result = await performAIImageEdit(effectivePrompt);
        const followUp = {
          id: Date.now() + 2,
          sender: 'ai',
          text: result.ok
            ? (wantsObjectRemoval
                ? "✅ Object removed! The background has been inpainted to match the scene."
                : "✅ Done! The image has been updated according to your request.")
            : `❌ ${result.error || 'AI edit failed.'}${result.error && result.error.includes('empty') ? ' Upload an image first, then try again.' : ''}`,
        };
        setAiMessages(prev => [...prev, followUp]);
        return;
      } finally {
        setProcessing(false);
      }
    }

    const aiMsg = { id: Date.now() + 1, sender: 'ai', text: response };
    setAiMessages(prev => [...prev, aiMsg]);

    if (action) {
      action();
    }

    } catch (err) {
      addAiReply("❌ Something went wrong: " + (err?.message || String(err)));
    } finally {
      setAiProcessing(false);
    }

    setTimeout(() => { if (aiChatRef.current) aiChatRef.current.scrollTop = aiChatRef.current.scrollHeight; }, 100);
  };

  // Handle AI input
  const handleAISubmit = (e) => {
    e.preventDefault();
    if (aiInput.trim() && !aiProcessing) {
      processAICommand(aiInput);
      setAiInput('');
    }
  };

  const loadImageFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = ctx || canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return;
    if (!ctx) setCtx(context);
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        context.drawImage(img, 0, 0);
        setImageData(context.getImageData(0, 0, canvas.width, canvas.height));
        setOriginalImage(img);
        setHasUploadedImage(true);
        addToHistory();
        setAiMessages(prev => [...prev, {
          id: Date.now(),
          sender: 'ai',
          text: `📸 Image uploaded! (${img.width}x${img.height}px)\n\nWhat would you like to do? Try:\n• "Remove background"\n• "Enhance image"\n• "Apply vintage filter"\n• "Make it brighter"`
        }]);
      };
      img.onerror = () => {};
      img.src = event.target.result;
    };
    reader.onerror = () => {};
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) loadImageFile(file);
    e.target.value = '';
  };

  const handleCanvasDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) loadImageFile(file);
  };

  // Export image
  const handleExport = () => {
    if (!hasUploadedImage || !canvasRef.current) return;
    try {
      const dataUrl = canvasRef.current.toDataURL();
      const link = document.createElement('a');
      link.download = 'edited-image.png';
      link.href = dataUrl;
      link.click();
      setAiMessages(prev => [...prev, { id: Date.now(), sender: 'ai', text: "✅ Image exported successfully! Check your downloads folder." }]);
    } catch (e) {
      if (e?.name === 'SecurityError' || /tainted/i.test(e?.message))
        setAiMessages(prev => [...prev, { id: Date.now(), sender: 'ai', text: "⚠️ Can't export: image is from another origin. Try re-uploading the image or use Generate to create a new one, then export." }]);
      else throw e;
    }
  };

  // Add layer
  const addLayer = () => {
    const newLayer = {
      id: `layer${layers.length + 1}`,
      name: `Layer ${layers.length + 1}`,
      visible: true,
      opacity: 100,
      blendMode: 'normal',
      locked: false,
    };
    setLayers([...layers, newLayer]);
    setSelectedLayer(newLayer.id);
  };

  // History — snapshot canvas to originalImage so adjustments/filters/curves apply to latest content (including drawings)
  const addToHistory = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = ctx || canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return;
    let data;
    try {
      data = canvas.toDataURL();
    } catch (e) {
      // Tainted canvas (cross-origin image): skip history so app doesn't crash
      const isTainted = e?.name === 'SecurityError' || /tainted|toDataURL/i.test(String(e?.message || ''));
      if (isTainted) return;
      throw e;
    }
    if (!data) return;
    const idx = historyIndexRef.current;
    setHistory(prev => prev.slice(0, idx + 1).concat(data));
    setHistoryIndex(idx + 1);
    const img = new Image();
    img.onload = () => setOriginalImage(img);
    img.src = data;
  };

  const undo = () => {
    if (historyIndex <= 0 || !ctx) return;
    const newIndex = historyIndex - 1;
    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas || !ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      setOriginalImage(img);
    };
    img.src = history[newIndex];
    setHistoryIndex(newIndex);
  };

  const redo = () => {
    if (historyIndex >= history.length - 1 || !ctx) return;
    const newIndex = historyIndex + 1;
    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas || !ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      setOriginalImage(img);
    };
    img.src = history[newIndex];
    setHistoryIndex(newIndex);
  };

  const toggleLayerVisibility = (layerId) => {
    setLayers(prev => prev.map(l => l.id === layerId ? { ...l, visible: !l.visible } : l));
  };

  const getSelectionBBox = (sel) => {
    if (!sel) return null;
    if (sel.type === 'lasso' && sel.points && sel.points.length > 0) {
      let minX = sel.points[0].x, minY = sel.points[0].y, maxX = minX, maxY = minY;
      sel.points.forEach(p => {
        minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
        minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
      });
      return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }
    if (sel.width > 0 && sel.height > 0) return { x: sel.x, y: sel.y, width: sel.width, height: sel.height };
    return null;
  };

  const applyRotateSelection = (degrees) => {
    const canvas = canvasRef.current;
    const context = ctx || canvas?.getContext('2d', { willReadFrequently: true });
    const bbox = getSelectionBBox(selection);
    if (!canvas || !context || !bbox || bbox.width <= 0 || bbox.height <= 0) return;
    const x = Math.max(0, Math.floor(bbox.x));
    const y = Math.max(0, Math.floor(bbox.y));
    const w = Math.min(canvas.width - x, Math.ceil(bbox.width));
    const h = Math.min(canvas.height - y, Math.ceil(bbox.height));
    if (w <= 0 || h <= 0) return;
    const imgData = context.getImageData(x, y, w, h);
    const src = document.createElement('canvas');
    src.width = w;
    src.height = h;
    const sctx = src.getContext('2d', { willReadFrequently: true });
    sctx.putImageData(imgData, 0, 0);
    const out = document.createElement('canvas');
    if (degrees === 90 || degrees === -90) {
      out.width = h;
      out.height = w;
    } else {
      out.width = w;
      out.height = h;
    }
    const octx = out.getContext('2d', { willReadFrequently: true });
    octx.save();
    if (degrees === 90) {
      octx.translate(h, 0);
      octx.rotate(Math.PI / 2);
    } else if (degrees === -90) {
      octx.translate(0, w);
      octx.rotate(-Math.PI / 2);
    } else if (degrees === 180) {
      octx.translate(w / 2, h / 2);
      octx.rotate(Math.PI);
      octx.translate(-w / 2, -h / 2);
    }
    octx.drawImage(src, 0, 0);
    octx.restore();
    context.save();
    if (selection.type === 'lasso') context.globalCompositeOperation = 'destination-out';
    context.clearRect(x, y, w, h);
    if (selection.type === 'lasso') context.globalCompositeOperation = 'source-over';
    if (degrees === 90 || degrees === -90) {
      context.drawImage(out, x, y, h, w);
      setSelection(selection.type === 'lasso' ? selection : { ...selection, x, y, width: h, height: w });
    } else {
      context.drawImage(out, x, y, w, h);
    }
    context.restore();
    addToHistory();
  };

  // Drawing (brush/eraser)
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => {
    const coords = getCanvasCoords(e);
    if (!coords) return;
    const context = ctx || canvasRef.current?.getContext('2d', { willReadFrequently: true });
    if (!context) return;

    if (selectedTool === 'select') {
      if (selectionMode === 'lasso') {
        lassoPointsRef.current = [coords];
        setSelectionDraft({ type: 'lasso', points: [coords] });
        setSelection(null);
      } else {
        selectionStartRef.current = coords;
        setSelectionDraft({ x: coords.x, y: coords.y, width: 0, height: 0 });
        setSelection(null);
      }
      return;
    }
    if (selectedTool === 'text') {
      setTextAnchor({ x: coords.x, y: coords.y });
      setTextDraft('');
      return;
    }
    if (selectedTool === 'gradient') {
      gradientStartRef.current = coords;
      return;
    }
    if (selectedTool === 'brush' || selectedTool === 'eraser') {
      setIsDrawing(true);
      lastDrawPos.current = coords;
      context.beginPath();
      context.moveTo(coords.x, coords.y);
    }
  };

  const draw = (e) => {
    const coords = getCanvasCoords(e);
    if (!coords) return;

    if (selectedTool === 'select') {
      if (lassoPointsRef.current) {
        lassoPointsRef.current.push(coords);
        setSelectionDraft({ type: 'lasso', points: [...lassoPointsRef.current] });
        return;
      }
      if (selectionStartRef.current) {
        const start = selectionStartRef.current;
        setSelectionDraft({
          x: Math.min(start.x, coords.x),
          y: Math.min(start.y, coords.y),
          width: Math.abs(coords.x - start.x),
          height: Math.abs(coords.y - start.y),
        });
        return;
      }
    }

    if (!isDrawing) return;
    if (!coords) return;
    const context = ctx || canvasRef.current?.getContext('2d', { willReadFrequently: true });
    if (!context) return;
    const from = lastDrawPos.current || coords;
    context.beginPath();
    context.moveTo(from.x, from.y);
    context.lineTo(coords.x, coords.y);
    if (selectedTool === 'brush') {
      const [r, g, b] = hexToRgb(brushColor);
      context.strokeStyle = `rgba(${r},${g},${b},${(brushOpacity / 100) * (brushFlow / 100)})`;
      context.lineWidth = brushSize;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.globalAlpha = 1;
      context.shadowColor = brushColor;
      context.shadowBlur = (100 - brushHardness) * 0.4;
      context.stroke();
      context.shadowBlur = 0;
      context.shadowColor = 'transparent';
    } else if (selectedTool === 'eraser') {
      context.globalCompositeOperation = 'destination-out';
      context.strokeStyle = `rgba(0,0,0,${brushOpacity / 100})`;
      context.lineWidth = brushSize;
      context.lineCap = 'round';
      context.shadowColor = 'rgba(0,0,0,0.5)';
      context.shadowBlur = (100 - brushHardness) * 0.4;
      context.stroke();
      context.shadowBlur = 0;
      context.shadowColor = 'transparent';
      context.globalCompositeOperation = 'source-over';
    }
    lastDrawPos.current = coords;
  };

  function hexToRgb(hex) {
    const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [0, 0, 0];
  }

  const commitTextToCanvas = () => {
    const canvas = canvasRef.current;
    const context = ctx || canvas?.getContext('2d', { willReadFrequently: true });
    if (!context || !canvas || !textAnchor) return;
    const str = (textDraft || 'Text').trim() || 'Text';
    context.save();
    const style = [textItalic && 'italic', textBold && 'bold'].filter(Boolean).join(' ');
    context.font = `${style ? style + ' ' : ''}${textFontSize}px ${textFontFamily}`;
    context.fillStyle = brushColor;
    context.textBaseline = 'top';
    context.fillText(str, textAnchor.x, textAnchor.y);
    context.restore();
    addToHistory();
    setTextAnchor(null);
    setTextDraft('');
  };

  const stopDrawing = (e) => {
    if (selectedTool === 'select') {
      if (lassoPointsRef.current && lassoPointsRef.current.length >= 3) {
        const pts = [...lassoPointsRef.current];
        pts.push(pts[0]); // close path
        setSelection({ type: 'lasso', points: pts });
        lassoPointsRef.current = null;
        setSelectionDraft(null);
        return;
      }
      if (selectionStartRef.current) {
        const draft = selectionDraft;
        if (draft && draft.width >= 2 && draft.height >= 2) {
          setSelection(selectionMode === 'ellipse'
            ? { type: 'ellipse', x: draft.x, y: draft.y, width: draft.width, height: draft.height }
            : { x: draft.x, y: draft.y, width: draft.width, height: draft.height });
        }
        selectionStartRef.current = null;
        setSelectionDraft(null);
        return;
      }
    }
    if (selectedTool === 'gradient' && gradientStartRef.current) {
      const end = e ? getCanvasCoords(e) : null;
      if (end) {
        const start = gradientStartRef.current;
        const context = ctx || canvasRef.current?.getContext('2d', { willReadFrequently: true });
        if (context) {
          const grad = context.createLinearGradient(start.x, start.y, end.x, end.y);
          const [r, g, b] = hexToRgb(brushColor);
          grad.addColorStop(0, brushColor);
          grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
          context.fillStyle = grad;
          context.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          addToHistory();
        }
      }
      gradientStartRef.current = null;
      return;
    }
    if (isDrawing) {
      setIsDrawing(false);
      addToHistory();
    }
  };

  // Apply filters and curves to canvas with REAL pixel manipulation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    try {
      if (originalImage) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(originalImage, 0, 0);
      }
      // Get image data for pixel manipulation (current canvas = base image or existing content)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Apply adjustments to actual pixel values
      for (let i = 0; i < data.length; i += 4) {
        let r = data[i];
        let g = data[i + 1];
        let b = data[i + 2];

        // Brightness adjustment (-100 to 100)
        if (adjustments.brightness !== 0) {
          const brightnessFactor = adjustments.brightness * 2.55;
          r = Math.max(0, Math.min(255, r + brightnessFactor));
          g = Math.max(0, Math.min(255, g + brightnessFactor));
          b = Math.max(0, Math.min(255, b + brightnessFactor));
        }

        // Contrast adjustment (-100 to 100)
        if (adjustments.contrast !== 0) {
          const contrastFactor = (259 * (adjustments.contrast + 255)) / (255 * (259 - adjustments.contrast));
          r = Math.max(0, Math.min(255, contrastFactor * (r - 128) + 128));
          g = Math.max(0, Math.min(255, contrastFactor * (g - 128) + 128));
          b = Math.max(0, Math.min(255, contrastFactor * (b - 128) + 128));
        }

        // Saturation adjustment (-100 to 100)
        if (adjustments.saturation !== 0) {
          const gray = 0.2989 * r + 0.5870 * g + 0.1140 * b;
          const saturationFactor = 1 + (adjustments.saturation / 100);
          r = Math.max(0, Math.min(255, gray + saturationFactor * (r - gray)));
          g = Math.max(0, Math.min(255, gray + saturationFactor * (g - gray)));
          b = Math.max(0, Math.min(255, gray + saturationFactor * (b - gray)));
        }

        // Exposure adjustment (-100 to 100)
        if (adjustments.exposure !== 0) {
          const exposureFactor = Math.pow(2, adjustments.exposure / 100);
          r = Math.max(0, Math.min(255, r * exposureFactor));
          g = Math.max(0, Math.min(255, g * exposureFactor));
          b = Math.max(0, Math.min(255, b * exposureFactor));
        }

        // Highlights adjustment
        if (adjustments.highlights !== 0) {
          const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
          if (luminance > 180) {
            const factor = 1 + (adjustments.highlights / 200);
            r = Math.max(0, Math.min(255, r * factor));
            g = Math.max(0, Math.min(255, g * factor));
            b = Math.max(0, Math.min(255, b * factor));
          }
        }

        // Shadows adjustment
        if (adjustments.shadows !== 0) {
          const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
          if (luminance < 75) {
            const factor = 1 + (adjustments.shadows / 200);
            r = Math.max(0, Math.min(255, r * factor));
            g = Math.max(0, Math.min(255, g * factor));
            b = Math.max(0, Math.min(255, b * factor));
          }
        }

        // Temperature adjustment (warm/cool)
        if (adjustments.temperature !== 0) {
          const temp = adjustments.temperature / 100;
          r = Math.max(0, Math.min(255, r + temp * 50));
          b = Math.max(0, Math.min(255, b - temp * 50));
        }

        // Vibrance adjustment
        if (adjustments.vibrance !== 0) {
          const max = Math.max(r, g, b);
          const avg = (r + g + b) / 3;
          const amt = ((Math.abs(max - avg) * 2 / 255) * (adjustments.vibrance / 100));
          if (r !== max) r = Math.max(0, Math.min(255, r + (max - r) * amt));
          if (g !== max) g = Math.max(0, Math.min(255, g + (max - g) * amt));
          if (b !== max) b = Math.max(0, Math.min(255, b + (max - b) * amt));
        }

        // Clarity/sharpness (edge enhancement)
        if (adjustments.clarity !== 0) {
          // Simple edge detection and enhancement
          const clarityFactor = adjustments.clarity / 100;
          r = Math.max(0, Math.min(255, r + clarityFactor * 20));
          g = Math.max(0, Math.min(255, g + clarityFactor * 20));
          b = Math.max(0, Math.min(255, b + clarityFactor * 20));
        }

        // Hue rotation
        if (adjustments.hue !== 0) {
          const angle = adjustments.hue * Math.PI / 180;
          const cosA = Math.cos(angle);
          const sinA = Math.sin(angle);
          const nr = r * (0.299 + 0.701 * cosA + 0.168 * sinA) + g * (0.587 - 0.587 * cosA + 0.330 * sinA) + b * (0.114 - 0.114 * cosA - 0.497 * sinA);
          const ng = r * (0.299 - 0.299 * cosA - 0.328 * sinA) + g * (0.587 + 0.413 * cosA + 0.035 * sinA) + b * (0.114 - 0.114 * cosA + 0.292 * sinA);
          const nb = r * (0.299 - 0.300 * cosA + 1.250 * sinA) + g * (0.587 - 0.588 * cosA - 1.050 * sinA) + b * (0.114 + 0.886 * cosA - 0.203 * sinA);
          r = Math.max(0, Math.min(255, nr));
          g = Math.max(0, Math.min(255, ng));
          b = Math.max(0, Math.min(255, nb));
        }

        // Apply filters
        if (filters.sharpen > 0) {
          // Sharpen increases contrast at edges
          const sharpenFactor = filters.sharpen / 100;
          r = Math.max(0, Math.min(255, r + sharpenFactor * 30));
          g = Math.max(0, Math.min(255, g + sharpenFactor * 30));
          b = Math.max(0, Math.min(255, b + sharpenFactor * 30));
        }

        if (filters.noise > 0) {
          // Add grain/noise
          const noise = (Math.random() - 0.5) * filters.noise * 2;
          r = Math.max(0, Math.min(255, r + noise));
          g = Math.max(0, Math.min(255, g + noise));
          b = Math.max(0, Math.min(255, b + noise));
        }

        if (filters.vignette > 0) {
          // Darken edges (vignette effect)
          const x = (i / 4) % canvas.width;
          const y = Math.floor((i / 4) / canvas.width);
          const dx = x - canvas.width / 2;
          const dy = y - canvas.height / 2;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const maxDistance = Math.sqrt(canvas.width * canvas.width + canvas.height * canvas.height) / 2;
          const vignetteFactor = 1 - (distance / maxDistance) * (filters.vignette / 100);
          r *= Math.max(0, Math.min(1, vignetteFactor));
          g *= Math.max(0, Math.min(1, vignetteFactor));
          b *= Math.max(0, Math.min(1, vignetteFactor));
        }

        // Curves (shadows = dark pixels, midtones = mid, highlights = bright)
        const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        if (curves.shadows !== 0 && luminance < 85) {
          const f = 1 + (curves.shadows / 100);
          r = Math.max(0, Math.min(255, r * f));
          g = Math.max(0, Math.min(255, g * f));
          b = Math.max(0, Math.min(255, b * f));
        }
        if (curves.midtones !== 0 && luminance >= 85 && luminance <= 170) {
          const f = 1 + (curves.midtones / 100);
          r = Math.max(0, Math.min(255, r * f));
          g = Math.max(0, Math.min(255, g * f));
          b = Math.max(0, Math.min(255, b * f));
        }
        if (curves.highlights !== 0 && luminance > 170) {
          const f = 1 + (curves.highlights / 100);
          r = Math.max(0, Math.min(255, r * f));
          g = Math.max(0, Math.min(255, g * f));
          b = Math.max(0, Math.min(255, b * f));
        }

        // Write modified pixels back
        data[i] = Math.round(r);
        data[i + 1] = Math.round(g);
        data[i + 2] = Math.round(b);
      }

      // Put modified image data back to canvas
      ctx.putImageData(imageData, 0, 0);

      // Apply blur as CSS filter (can't be done with pixel manipulation efficiently)
      if (filters.blur > 0) {
        canvas.style.filter = `blur(${filters.blur * 0.5}px)`;
      } else {
        canvas.style.filter = 'none';
      }
    } catch (_) {
      // ignore (e.g. canvas tainted or zero-size)
    }
  }, [adjustments, filters, curves, originalImage, ctx]);

  // Keyboard shortcuts (Photoshop-like)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z') {
          e.preventDefault();
          if (e.shiftKey) redo();
          else undo();
        }
        if (e.key === 's') {
          e.preventDefault();
          handleExport();
        }
        return;
      }
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      switch (e.key.toLowerCase()) {
        case 'b': setSelectedTool('brush'); e.preventDefault(); break;
        case 'e': setSelectedTool('eraser'); e.preventDefault(); break;
        case 'v': setSelectedTool('select'); e.preventDefault(); break;
        case 'g': setSelectedTool('gradient'); e.preventDefault(); break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, historyIndex, history.length]);

  const updateLayer = (layerId, updates) => {
    setLayers(prev => prev.map(l => l.id === layerId ? { ...l, ...updates } : l));
  };

  const deleteLayer = (layerId) => {
    if (layers.length <= 1) return;
    setLayers(prev => prev.filter(l => l.id !== layerId));
    if (selectedLayer === layerId) setSelectedLayer(layers[0]?.id || 'layer1');
  };

  const duplicateLayer = (layerId) => {
    const layer = layers.find(l => l.id === layerId);
    if (!layer) return;
    const newId = `layer-${Date.now()}`;
    const newLayer = {
      ...layer,
      id: newId,
      name: layer.name.replace(/\s*copy(\s*\d*)*$/i, '').trim() + ' copy',
    };
    const idx = layers.findIndex(l => l.id === layerId);
    const next = idx < 0 ? layers.length : idx + 1;
    setLayers(prev => [...prev.slice(0, next), newLayer, ...prev.slice(next)]);
    setSelectedLayer(newId);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      className={`pro-image-editor-v2${embedded ? ' pro-image-editor-v2-embedded' : ''}${isFullscreen ? ' pro-image-editor-v2-fullscreen' : ''}`}
    >
      {embedded && (
        <div className="editor-embedded-bar">
          {embeddedWorkspaceTitle ? (
            <span className="editor-embedded-workspace-title">{embeddedWorkspaceTitle}</span>
          ) : null}
          {onSwitchToVideoEditor && (
            <button type="button" className="editor-header-switch-btn" onClick={onSwitchToVideoEditor} title="Switch to Video Editor" aria-label="Switch to Video Editor">🎬 Video</button>
          )}
          {onSwitchToChat && (
            <button type="button" className="editor-header-switch-btn" onClick={onSwitchToChat} title="Back to Chat" aria-label="Back to Chat">💬 Chat</button>
          )}
          <button type="button" className="editor-fullscreen-btn" onClick={toggleFullscreen} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
          </button>
        </div>
      )}
      {!embedded && (
        <div className="editor-header">
          <div className="editor-header-brand">
            <span className="editor-header-logo">◇</span>
            <div>
              <h1 className="editor-header-title">Zolaid Pro Image Editor</h1>
              <p className="editor-header-subtitle">Layers • Adjustments • Curves • AI Assistant</p>
            </div>
          </div>
          <div className="header-tools">
            <span className="feature-badge">Layers</span>
            <span className="feature-badge">Curves</span>
            <span className="feature-badge">Blend</span>
<button type="button" onClick={toggleFullscreen} className="editor-fullscreen-btn" title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
          </button>
            {onSwitchToVideoEditor && (
              <button type="button" className="editor-header-switch-btn" onClick={onSwitchToVideoEditor} title="Switch to Video Editor" aria-label="Switch to Video Editor">🎬 Video</button>
            )}
            {onSwitchToChat && (
              <button type="button" className="editor-header-switch-btn" onClick={onSwitchToChat} title="Back to Chat" aria-label="Back to Chat">💬 Chat</button>
            )}
            <button type="button" onClick={onClose} className="close-btn" title="Close" aria-label="Close editor">✕</button>
          </div>
        </div>
      )}

      {/* Photoshop-style options bar */}
      <div className="options-bar">
        <span className="options-bar-tool">{selectedTool === 'select' ? 'Select' : selectedTool === 'brush' ? 'Brush' : selectedTool === 'eraser' ? 'Eraser' : selectedTool === 'text' ? 'Text' : selectedTool === 'gradient' ? 'Gradient' : 'Move'}</span>
        {selectedTool === 'select' && (
          <>
            <div className="options-bar-group">
              <label>Mode</label>
              <select value={selectionMode} onChange={(e) => setSelectionMode(e.target.value)} className="options-bar-select">
                <option value="rect">Rect</option>
                <option value="ellipse">Ellipse</option>
                <option value="lasso">Lasso</option>
              </select>
            </div>
            <span className="options-bar-hint">{selectionMode === 'lasso' ? 'Drag to draw freehand; release to close. Esc to deselect.' : selectionMode === 'ellipse' ? 'Drag to draw ellipse. Esc to deselect.' : 'Drag a rectangle, or ask AI to replace selection. Esc to deselect.'}</span>
          </>
        )}
        {(selectedTool === 'brush' || selectedTool === 'eraser') && (
          <>
            <div className="options-bar-group">
              <label>Size</label>
              <input type="range" min="1" max="200" value={brushSize} onChange={(e) => setBrushSize(Number(e.target.value))} />
              <span>{brushSize}px</span>
            </div>
            <div className="options-bar-group">
              <label>Opacity</label>
              <input type="range" min="1" max="100" value={brushOpacity} onChange={(e) => setBrushOpacity(Number(e.target.value))} />
              <span>{brushOpacity}%</span>
            </div>
            <div className="options-bar-group">
              <label>Flow</label>
              <input type="range" min="1" max="100" value={brushFlow} onChange={(e) => setBrushFlow(Number(e.target.value))} />
              <span>{brushFlow}%</span>
            </div>
            {selectedTool === 'brush' && (
              <div className="options-bar-group">
                <label>Color</label>
                <input type="color" value={brushColor} onChange={(e) => setBrushColor(e.target.value)} className="options-bar-color" />
              </div>
            )}
          </>
        )}
        {selectedTool === 'gradient' && (
          <div className="options-bar-group">
            <label>Color</label>
            <input type="color" value={brushColor} onChange={(e) => setBrushColor(e.target.value)} className="options-bar-color" />
          </div>
        )}
        {selectedTool === 'text' && (
          <>
            <div className="options-bar-group">
              <label>Font</label>
              <select value={textFontFamily} onChange={(e) => setTextFontFamily(e.target.value)} className="options-bar-select">
                <option value="sans-serif">Sans-serif</option>
                <option value="serif">Serif</option>
                <option value="monospace">Monospace</option>
                <option value="Arial">Arial</option>
                <option value="Georgia">Georgia</option>
                <option value="'Courier New'">Courier New</option>
                <option value="'Times New Roman'">Times New Roman</option>
              </select>
            </div>
            <div className="options-bar-group">
              <label>Size</label>
              <input type="number" min="8" max="400" value={textFontSize} onChange={(e) => setTextFontSize(Number(e.target.value) || 24)} className="options-bar-number" />
              <span>px</span>
            </div>
            <div className="options-bar-group options-bar-buttons">
              <button type="button" className={`options-bar-toggle ${textBold ? 'active' : ''}`} onClick={() => setTextBold(!textBold)} title="Bold">B</button>
              <button type="button" className={`options-bar-toggle ${textItalic ? 'active' : ''}`} onClick={() => setTextItalic(!textItalic)} title="Italic">I</button>
            </div>
            <div className="options-bar-group">
              <label>Color</label>
              <input type="color" value={brushColor} onChange={(e) => setBrushColor(e.target.value)} className="options-bar-color" />
            </div>
            {textAnchor && (
              <>
                <input
                  ref={textInputRef}
                  type="text"
                  value={textDraft}
                  onChange={(e) => setTextDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitTextToCanvas(); if (e.key === 'Escape') { setTextAnchor(null); setTextDraft(''); } }}
                  placeholder="Type text..."
                  className="options-bar-text-input"
                />
                <button type="button" className="options-bar-btn" onClick={commitTextToCanvas}>Add text</button>
              </>
            )}
            {!textAnchor && <span className="options-bar-hint">Click on canvas to place text.</span>}
          </>
        )}
      </div>

      <div className="editor-layout">
        {/* Left Sidebar - collapsible; when closed: only + icon for minimal view (image + typing box) */}
        <div className={`sidebar-left ${leftSidebarOpen ? 'sidebar-left-open' : 'sidebar-left-collapsed'}`}>
          <button
            type="button"
            className="sidebar-left-toggle"
            onClick={() => setLeftSidebarOpen(!leftSidebarOpen)}
            title={leftSidebarOpen ? 'Hide tools (minimal view)' : 'Show tools & options'}
            aria-label={leftSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {leftSidebarOpen ? '◀' : '+'}
          </button>
          {leftSidebarOpen && (
          <div className="sidebar-left-content">
          <div className="section">
            <h3>Tools</h3>
            <div className="tool-grid">
              <button
                className={`tool-btn ${selectedTool === 'select' ? 'active' : ''}`}
                onClick={() => setSelectedTool('select')}
                title="Select (V)"
              >
                ↖️
              </button>
              <button
                className={`tool-btn ${selectedTool === 'brush' ? 'active' : ''}`}
                onClick={() => setSelectedTool('brush')}
                title="Brush (B)"
              >
                🖌️
              </button>
              <button
                className={`tool-btn ${selectedTool === 'eraser' ? 'active' : ''}`}
                onClick={() => setSelectedTool('eraser')}
                title="Eraser (E)"
              >
                🧹
              </button>
              <button
                className={`tool-btn ${selectedTool === 'text' ? 'active' : ''}`}
                onClick={() => setSelectedTool('text')}
                title="Text (T)"
              >
                T
              </button>
              <button
                className={`tool-btn ${selectedTool === 'shape' ? 'active' : ''}`}
                onClick={() => setSelectedTool('shape')}
                title="Shape (U)"
              >
                ▢
              </button>
              <button
                className={`tool-btn ${selectedTool === 'gradient' ? 'active' : ''}`}
                onClick={() => setSelectedTool('gradient')}
                title="Gradient (G)"
              >
                ▤
              </button>
              <button
                className={`tool-btn ${selectedTool === 'magic' ? 'active' : ''}`}
                onClick={() => setSelectedTool('magic')}
                title="Magic Wand (W)"
              >
                ✨
              </button>
            </div>
          </div>

          <div className="section">
            <h3>Curves</h3>
            <div className="curves-controls">
              <div className="curve-row">
                <label>Shadows</label>
                <input type="range" min="-50" max="50" value={curves.shadows} onChange={(e) => setCurves(c => ({ ...c, shadows: Number(e.target.value) }))} />
              </div>
              <div className="curve-row">
                <label>Midtones</label>
                <input type="range" min="-50" max="50" value={curves.midtones} onChange={(e) => setCurves(c => ({ ...c, midtones: Number(e.target.value) }))} />
              </div>
              <div className="curve-row">
                <label>Highlights</label>
                <input type="range" min="-50" max="50" value={curves.highlights} onChange={(e) => setCurves(c => ({ ...c, highlights: Number(e.target.value) }))} />
              </div>
            </div>
          </div>

          <div className="section">
            <h3>Import</h3>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              id="image-upload"
            />
            <label htmlFor="image-upload" className="upload-btn" aria-label="Upload image from computer">
              Upload image
            </label>
          </div>

          <div className="section">
            <h3>Layers</h3>
            <div className="layers-list">
              {layers.map(layer => (
                <div
                  key={layer.id}
                  className={`layer-item ${selectedLayer === layer.id ? 'active' : ''}`}
                  onClick={() => setSelectedLayer(layer.id)}
                >
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(ev) => { ev.stopPropagation(); toggleLayerVisibility(layer.id); }}
                    onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ev.stopPropagation(); toggleLayerVisibility(layer.id); } }}
                    title={layer.visible ? 'Hide layer' : 'Show layer'}
                    className="layer-eye"
                  >
                    {layer.visible ? '👁' : '👁‍🗨'}
                  </span>
                  <span className="layer-name">{layer.name}</span>
                  {layer.locked && <span className="layer-lock">🔒</span>}
                  <div className="layer-opacity-wrap" onClick={ev => ev.stopPropagation()}>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={layer.opacity}
                      onChange={(e) => updateLayer(layer.id, { opacity: Number(e.target.value) })}
                      className="layer-opacity"
                      title="Opacity"
                    />
                  </div>
                  <select
                    value={layer.blendMode}
                    onChange={(e) => updateLayer(layer.id, { blendMode: e.target.value })}
                    onClick={ev => ev.stopPropagation()}
                    className="layer-blend"
                    title="Blend mode"
                  >
                    {BLEND_MODES.map(mode => (
                      <option key={mode} value={mode}>{mode}</option>
                    ))}
                  </select>
                  <button type="button" className="layer-duplicate" onClick={(ev) => { ev.stopPropagation(); duplicateLayer(layer.id); }} title="Duplicate layer">⎘</button>
                  <button type="button" className="layer-delete" onClick={(ev) => { ev.stopPropagation(); deleteLayer(layer.id); }} title="Delete layer">⌫</button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addLayer} className="add-layer-btn">New layer</button>
          </div>

          {/* Quick Actions — grouped: Light & color, Effects, AI, Reset */}
          <div className="section quick-actions-left">
            <h3>Quick Actions</h3>
            <div className="quick-actions-group">
              <div className="quick-actions-group-label">Light & color</div>
              <div className="quick-actions-grid">
                <button type="button" onClick={() => setAdjustments(prev => ({ ...prev, brightness: Math.min(100, prev.brightness + 20) }))} className="quick-btn" disabled={aiProcessing} title="Increase brightness">Brighter</button>
                <button type="button" onClick={() => setAdjustments(prev => ({ ...prev, contrast: Math.min(100, prev.contrast + 20) }))} className="quick-btn" disabled={aiProcessing} title="Increase contrast">Contrast</button>
                <button type="button" onClick={() => setAdjustments(prev => ({ ...prev, saturation: Math.min(100, prev.saturation + 30) }))} className="quick-btn" disabled={aiProcessing} title="Increase saturation">Saturation</button>
                <button type="button" onClick={() => setAdjustments(prev => ({ ...prev, hue: (prev.hue + 45) % 360 }))} className="quick-btn" disabled={aiProcessing} title="Rotate hue">Hue</button>
              </div>
            </div>
            <div className="quick-actions-group">
              <div className="quick-actions-group-label">Effects</div>
              <div className="quick-actions-grid">
                <button type="button" onClick={() => setFilters(prev => ({ ...prev, sharpen: Math.min(100, prev.sharpen + 20) }))} className="quick-btn" disabled={aiProcessing} title="Sharpen">Sharpen</button>
              </div>
            </div>
            <div className="quick-actions-group">
              <div className="quick-actions-group-label">AI</div>
              <div className="quick-actions-grid">
                <button type="button" onClick={() => processAICommand('Enhance image')} className="quick-btn quick-btn-ai" disabled={aiProcessing} title="Auto-enhance">Enhance</button>
                <button type="button" onClick={() => processAICommand('Remove background')} className="quick-btn quick-btn-ai" disabled={aiProcessing} title="Remove background">Remove BG</button>
                <button type="button" onClick={() => processAICommand('Apply vintage filter')} className="quick-btn quick-btn-ai" disabled={aiProcessing} title="Vintage look">Vintage</button>
                <button type="button" onClick={() => processAICommand('Upscale 4x')} className="quick-btn quick-btn-ai" disabled={aiProcessing} title="Upscale 4x">Upscale</button>
              </div>
            </div>
            <div className="quick-actions-group">
              <button
                type="button"
                onClick={() => {
                  setAdjustments({
                    brightness: 0,
                    contrast: 0,
                    saturation: 0,
                    hue: 0,
                    temperature: 0,
                    exposure: 0,
                    highlights: 0,
                    shadows: 0,
                    clarity: 0,
                    vibrance: 0,
                  });
                  setFilters({ blur: 0, sharpen: 0, noise: 0, vignette: 0, grain: 0 });
                }}
                className="quick-btn quick-btn-reset"
                disabled={aiProcessing}
                title="Reset all adjustments and filters"
              >
                Reset all
              </button>
            </div>
            <div className="quick-values-panel">
              <strong>Current values</strong>
              <div>Brightness {adjustments.brightness} · Contrast {adjustments.contrast} · Sat {adjustments.saturation}</div>
              <div>Hue {adjustments.hue}° · Sharpen {filters.sharpen} · Blur {filters.blur}</div>
            </div>
          </div>
          <div className="section export-section-left">
            <h3>Export</h3>
            <button type="button" className="export-btn" onClick={handleExport} disabled={!hasUploadedImage} title={hasUploadedImage ? 'Download image' : 'Import or generate an image first'}>
              Download image
            </button>
          </div>
          </div>
          )}
        </div>

        {/* Center - Canvas */}
        <div className="editor-center">
          <div
            className="canvas-container"
            onDrop={handleCanvasDrop}
            onDragOver={(e) => e.preventDefault()}
            onMouseDown={(e) => {
              if (e.button === 1 || (e.button === 0 && selectedTool === 'select' && e.altKey)) {
                setIsPanning(true);
                panStartRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
              }
            }}
            onMouseMove={(e) => {
              if (isPanning && panStartRef.current) {
                setPan({ x: e.clientX - panStartRef.current.x, y: e.clientY - panStartRef.current.y });
              }
            }}
            onMouseUp={() => { setIsPanning(false); panStartRef.current = null; }}
            onMouseLeave={() => { setIsPanning(false); panStartRef.current = null; }}
            style={{ cursor: isPanning ? 'grabbing' : selectedTool === 'select' ? 'grab' : 'crosshair' }}
          >
            <div className="canvas-inner" style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom / 100})`, transformOrigin: 'center center' }}>
              <canvas
                ref={canvasRef}
                className="main-canvas"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
              />
              <div
                className="selection-overlay"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: canvasRef.current?.width ?? 800,
                  height: canvasRef.current?.height ?? 600,
                  pointerEvents: 'none',
                }}
              >
                <svg viewBox={`0 0 ${Math.max(1, canvasRef.current?.width ?? 800)} ${Math.max(1, canvasRef.current?.height ?? 600)}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%', display: 'block' }}>
                  {selection && selection.type === 'lasso' && selection.points && selection.points.length >= 2 && (
                    <polygon points={selection.points.map(p => `${p.x},${p.y}`).join(' ')} fill="rgba(0,170,255,0.15)" stroke="#0af" strokeWidth="2" strokeDasharray="8,4" />
                  )}
                  {selection && selection.type === 'ellipse' && selection.width > 0 && selection.height > 0 && (
                    <ellipse cx={selection.x + selection.width / 2} cy={selection.y + selection.height / 2} rx={selection.width / 2} ry={selection.height / 2} fill="rgba(0,170,255,0.15)" stroke="#0af" strokeWidth="2" strokeDasharray="8,4" />
                  )}
                  {selection && selection.type !== 'lasso' && selection.type !== 'ellipse' && selection.width > 0 && selection.height > 0 && (
                    <rect x={selection.x} y={selection.y} width={selection.width} height={selection.height} fill="rgba(0,170,255,0.15)" stroke="#0af" strokeWidth="2" strokeDasharray="8,4" />
                  )}
                  {selectionDraft && selectionDraft.type === 'lasso' && selectionDraft.points && selectionDraft.points.length >= 2 && (
                    <polyline points={selectionDraft.points.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="#0af" strokeWidth="2" strokeDasharray="8,4" />
                  )}
                  {selectionDraft && selectionMode === 'ellipse' && selectionDraft.width > 0 && selectionDraft.height > 0 && (
                    <ellipse cx={selectionDraft.x + selectionDraft.width / 2} cy={selectionDraft.y + selectionDraft.height / 2} rx={selectionDraft.width / 2} ry={selectionDraft.height / 2} fill="rgba(0,170,255,0.1)" stroke="#0af" strokeWidth="2" strokeDasharray="8,4" />
                  )}
                  {selectionDraft && selectionDraft.type !== 'lasso' && selectionMode !== 'ellipse' && selectionDraft.width > 0 && selectionDraft.height > 0 && (
                    <rect x={selectionDraft.x} y={selectionDraft.y} width={selectionDraft.width} height={selectionDraft.height} fill="rgba(0,170,255,0.1)" stroke="#0af" strokeWidth="2" strokeDasharray="8,4" />
                  )}
                </svg>
              </div>
            </div>
            {!hasUploadedImage && (
              <div className="canvas-empty-overlay" aria-hidden="true">
                <div className="canvas-empty-icon">🖼️</div>
                <p className="canvas-empty-title">Import an image or generate one</p>
                <p className="canvas-empty-hint">Drag and drop here, use <strong>Upload image</strong> in the sidebar, or <strong>Generate</strong> (Nano Banana) — the placeholder hides when your image is on the canvas.</p>
              </div>
            )}
            {processing && (
              <div className="canvas-overlay">
                <div className="processing-spinner"></div>
                <p>AI is processing your image...</p>
                {imageEditModel === 'nano-banana' && (
                  <p style={{ fontSize: 12, opacity: 0.9, marginTop: 4 }}>
                    {nanoImageSpeed === 'quality' ? 'Higher quality — often ~1–2 min' : nanoImageSpeed === 'balanced' ? 'Usually under ~1 min' : 'Fast mode — often ~20–60s'}
                  </p>
                )}
                {imageEditModel === 'photoshop' && (
                  <p style={{ fontSize: 12, opacity: 0.9, marginTop: 4 }}>
                    Adobe Firefly — Generative Fill is async; often 1–3 minutes.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Typing box below the image */}
          <form onSubmit={handleAISubmit} className="ai-typing-bar">
            <select
              value={imageEditModel}
              onChange={(e) => setImageEditModel(e.target.value)}
              disabled={aiProcessing}
              className="ai-typing-bar-model"
              aria-label="AI model"
              title={
                imageEditModel === 'nano-banana' && falStatus && !falStatus.ok
                  ? (falStatus.error || 'Check FAL_KEY')
                  : imageEditModel === 'photoshop' && photoshopStatus && !photoshopStatus.ok
                    ? (photoshopStatus.error || 'Check Adobe credentials')
                    : 'DALL·E: OpenAI. Nano Banana: fal.ai. Photoshop: Adobe Firefly Generative Fill.'
              }
            >
              <option value="dalle">DALL·E (OpenAI)</option>
              <option value="nano-banana">Nano Banana (fal.ai)</option>
              {photoshopImageEditCap && (
                <option value="photoshop">Adobe Firefly (Photoshop)</option>
              )}
            </select>
            {imageEditModel === 'nano-banana' && (
              <select
                value={nanoImageSpeed}
                onChange={(e) => setNanoImageSpeed(e.target.value)}
                disabled={aiProcessing}
                className="ai-typing-bar-model"
                aria-label="Nano Banana speed"
                title="Fast: quickest, smaller output. Balanced: 1K JPEG. Quality: 1K PNG."
              >
                <option value="fast">⚡ Fast</option>
                <option value="balanced">Balanced</option>
                <option value="quality">Quality</option>
              </select>
            )}
            {imageEditModel === 'nano-banana' && falStatus && (
              <span className={`ai-typing-bar-fal-status ${falStatus.ok ? 'connected' : 'warning'}`} title={falStatus.ok ? 'FAL key connected' : (falStatus.error || 'Check FAL_KEY in backend/.env')}>
                {falStatus.ok ? '✓ Connected' : `⚠ ${(falStatus.error || 'Check key').slice(0, 30)}${(falStatus.error && falStatus.error.length > 30) ? '…' : ''}`}
              </span>
            )}
            {imageEditModel === 'photoshop' && photoshopStatus && (
              <span className={`ai-typing-bar-fal-status ${photoshopStatus.ok ? 'connected' : 'warning'}`} title={photoshopStatus.ok ? 'Adobe IMS token OK' : (photoshopStatus.error || 'Check backend/.env.apps')}>
                {photoshopStatus.ok ? '✓ Adobe' : `⚠ ${(photoshopStatus.error || 'Check Adobe').slice(0, 28)}${(photoshopStatus.error && photoshopStatus.error.length > 28) ? '…' : ''}`}
              </span>
            )}
            <input
              type="text"
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              placeholder={imageEditModel === 'nano-banana' ? 'Describe edit, or describe image to generate...' : imageEditModel === 'photoshop' ? 'Describe what to generate in masked areas (or whole image)...' : 'Describe edit or ask AI...'}
              disabled={aiProcessing}
              className="ai-typing-bar-input"
              aria-label="Message AI"
            />
            {imageEditModel === 'nano-banana' && (
              <button
                type="button"
                disabled={aiProcessing || !aiInput.trim()}
                className="ai-typing-bar-generate"
                aria-label="Generate image from text"
                title="Generate a new image from your description (text-to-image)"
                onClick={async () => {
                  const prompt = aiInput.trim();
                  if (!prompt || aiProcessing) return;
                  setAiInput('');
                  setAiProcessing(true);
                  setAiMessages(prev => [...prev, { id: Date.now(), sender: 'user', text: prompt }]);
                  setAiMessages(prev => [...prev, { id: Date.now() + 1, sender: 'ai', text: nanoImageSpeed === 'quality' ? '🔄 Generating… quality mode (~1–2 min).' : nanoImageSpeed === 'balanced' ? '🔄 Generating… usually under ~1 min.' : '🔄 Generating… fast mode (~20–60s).' }]);
                  setProcessing(true);
                  try {
                    const result = await performAIGenerate(prompt);
                    setAiMessages(prev => [...prev, { id: Date.now() + 2, sender: 'ai', text: result.ok ? '✅ Image generated!' : `❌ ${result.error || 'Generate failed.'}` }]);
                  } finally {
                    setProcessing(false);
                    setAiProcessing(false);
                  }
                  setTimeout(() => { if (aiChatRef.current) aiChatRef.current.scrollTop = aiChatRef.current.scrollHeight; }, 100);
                }}
              >
                Generate
              </button>
            )}
            <button type="submit" disabled={aiProcessing || !aiInput.trim()} className="ai-typing-bar-send" aria-label="Send" title="Send">
              Send
            </button>
          </form>

          <div className="canvas-controls">
            <button type="button" onClick={undo} disabled={!ctx || historyIndex <= 0} title="Undo (Ctrl+Z)" aria-label="Undo">↶ Undo</button>
            <button type="button" onClick={redo} disabled={!ctx || historyIndex >= history.length - 1} title="Redo (Ctrl+Shift+Z)" aria-label="Redo">↷ Redo</button>
            {hasValidSelection(selection) && (
              <>
                <span className="canvas-controls-sep" aria-hidden="true">|</span>
                <button type="button" onClick={() => applyRotateSelection(-90)} title="Rotate selection 90° CCW" aria-label="Rotate selection 90° left">↺ 90°</button>
                <button type="button" onClick={() => applyRotateSelection(90)} title="Rotate selection 90° CW" aria-label="Rotate selection 90° right">↻ 90°</button>
                <button type="button" onClick={() => applyRotateSelection(180)} title="Rotate selection 180°" aria-label="Rotate selection 180°">↻ 180°</button>
              </>
            )}
            <span className="canvas-controls-sep" aria-hidden="true">|</span>
            <button type="button" onClick={() => setZoom(Math.max(10, zoom - 10))} title="Zoom out" aria-label="Zoom out">−</button>
            <span className="zoom-value" aria-hidden="true">{zoom}%</span>
            <button type="button" onClick={() => setZoom(Math.min(400, zoom + 10))} title="Zoom in" aria-label="Zoom in">+</button>
            <button type="button" onClick={() => { setZoom(100); setPan({ x: 0, y: 0 }); }} title="Fit to view" aria-label="Fit to view">Fit</button>
          </div>
        </div>
      </div>
    </div>
  );
}
